import React, { useState, useEffect, useRef } from 'react';
import { Check, Shield, Clock, Award, Scale, FileCheck, Phone, Mail, MapPin, ChevronDown, Search, X } from 'lucide-react';
import { ConsultationForm } from '../components/ConsultationForm';
import { fetchGeoLocation, submitLead, getCountryFromGeo, validatePhoneNumber } from '../services/api';
import { countriesData } from '../data/countries';
import { useNavigate } from 'react-router-dom';
import { openChat } from '../utils/chat';
import '../styles/ConsultationForm.css';

export const TrademarkLandingPage = () => {
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formScrolled, setFormScrolled] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: 'US',
    moreInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [geoData, setGeoData] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState('United States');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDetectingCountry, setIsDetectingCountry] = useState(true);
  const dropdownRef = useRef(null);
  const countryButtonRef = useRef(null);
  
  // Typing animation state
  const [typedText, setTypedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);

  const words = ['Brand Name', 'Logo', 'Slogan'];

  // Fetch geolocation on component mount
  useEffect(() => {
    const loadGeoLocation = async () => {
      try {
        const geoResponse = await fetchGeoLocation();
        if (geoResponse?.isSuccess) {
          setGeoData(geoResponse);
          const countryInfo = getCountryFromGeo(geoResponse);
          setDetectedCountry(countryInfo.name);
          
          const country = countriesData.find(c => c.code === countryInfo.code);
          if (country) {
            setSelectedCountry(country);
            setFormData(prev => ({ ...prev, countryCode: country.code }));
          }
        }
      } catch (error) {
        console.log('IP detection failed, using default US');
      } finally {
        // Default fallback to US
        if (!selectedCountry) {
          const defaultCountry = countriesData.find(c => c.code === 'US');
          setSelectedCountry(defaultCountry);
          setFormData(prev => ({ ...prev, countryCode: 'US' }));
        }
        setIsDetectingCountry(false);
      }
    };
    
    loadGeoLocation();
  }, []);

  // Typing animation effect
  useEffect(() => {
    const handleTyping = () => {
      const currentWordIndex = loopNum % words.length;
      const currentWord = words[currentWordIndex];

      if (isDeleting) {
        setTypedText(currentWord.substring(0, typedText.length - 1));
        setTypingSpeed(50);
      } else {
        setTypedText(currentWord.substring(0, typedText.length + 1));
        setTypingSpeed(150);
      }

      if (!isDeleting && typedText === currentWord) {
        // Pause at end before deleting
        setTimeout(() => setIsDeleting(true), 2000);
        setTypingSpeed(2000);
      } else if (isDeleting && typedText === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
        setTypingSpeed(500);
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [typedText, isDeleting, loopNum, typingSpeed, words]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        countryButtonRef.current &&
        !countryButtonRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown]);

  useEffect(() => {
    const handleScroll = () => {
      const formSection = document.getElementById('lead-form');
      if (formSection) {
        const rect = formSection.getBoundingClientRect();
        setFormScrolled(rect.top < window.innerHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openForm = () => {
    setIsFormOpen(true);
  };

  const scrollToForm = () => {
    const formSection = document.getElementById('lead-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setFormData(prev => ({ ...prev, countryCode: country.code }));
    setShowCountryDropdown(false);
    setSearchQuery('');
  };

  const filteredCountries = countriesData.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.phone_code.includes(searchQuery)
  );

  const popularCountries = filteredCountries.filter(c => c.popular);
  const allCountries = filteredCountries.filter(c => !c.popular);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate phone number in real-time
    if (name === 'phone' && selectedCountry) {
      const validation = validatePhoneNumber(value, selectedCountry);
      setPhoneError(validation.isValid ? '' : validation.message);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    // Validate phone number before submission
    if (selectedCountry && formData.phone) {
      const validation = validatePhoneNumber(formData.phone, selectedCountry);
      if (!validation.isValid) {
        setPhoneError(validation.message);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Prepare lead data
      const leadData = {
        name: formData.fullName,
        email: formData.email,
        countryDialCode: selectedCountry?.phone_code || '1',
        country: selectedCountry?.code || 'US',
        phoneNumber: formData.phone,
        description: formData.moreInfo ? `${formData.moreInfo} | Source: PPC Landing Page` : 'Trademark registration inquiry | Source: PPC Landing Page'
      };

      // Submit lead
      const result = await submitLead(leadData, geoData);

      if (result.success && result.statusCode === 200) {
        // Redirect to application page with customer ID
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        // Show error message
        setErrorMessage(result.message);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const pricingPackages = [
    {
      name: 'Essentials',
      price: '$45',
      priceNote: '+ Federal Filing Fees ($350/class)',
      description: 'Basic trademark filing service',
      features: [
        'Case Review',
        'Basic Trademark Search',
        'Trademark Secured',
        '24/7 Secure Online Access',
        'Cease & Desist Letter Template',
        'Free Infringement Alerts (1 Month)'
      ],
      popular: false
    },
    {
      name: 'Enhanced',
      price: '$149',
      priceNote: '+ Federal Filing Fees ($350/class)',
      description: 'Enhanced filing with comprehensive search',
      features: [
        'Case Review by TM Expert',
        'Trademark Monitoring (3 Months)',
        '24-Hour Expedited Processing',
        'Trademark Secured',
        'Attorney Pre-Filing Letter',
        'Free Infringement Alerts (1 Month)',
        'Dedicated Client Relations Attorney'
      ],
      popular: true
    },
    {
      name: 'Elite',
      price: '$299',
      priceNote: '+ Federal Filing Fees ($350/class)',
      description: 'Comprehensive filing with full legal support',
      features: [
        'Attorney Case Review & Filing',
        'Trademark Monitoring (12 Months)',
        'Direct Hit Search',
        'Refusal Risk Evaluation',
        'Comprehensive Search (50 States + Social Media/Domains)',
        'Litigant Scan',
        'Opposition & Litigation Support',
        'Dedicated Attorney',
        '100% Approval Guaranteed'
      ],
      popular: false
    },
    {
      name: 'Exclusive',
      price: '$899',
      priceNote: '+ Federal Filing Fees ($350/class)',
      description: 'The most comprehensive trademark protection',
      features: [
        'Everything in Elite',
        'Trademark Monitoring (12 Months)',
        'Complete Paperwork Filing',
        'International Trademark Assistance',
        'Global Risk Assessment (100+ Countries)',
        'Free Infringement Alerts (3 Months)',
        'Priority Legal Support',
        '100% Approval Guarantee'
      ],
      popular: false
    }
  ];

  const testimonials = [
    {
      quote: "Trademark Liberty made the whole process seamless! I got my approval in weeks and felt confident every step of the way.",
      author: "Amanda R.",
      role: "eCommerce Seller"
    },
    {
      quote: "The attorneys were knowledgeable and responsive. They handled everything from search to filing. Highly recommend!",
      author: "Michael T.",
      role: "Tech Startup Founder"
    },
    {
      quote: "Transparent pricing and excellent service. They protected my brand name and helped me register on Amazon Brand Registry.",
      author: "Sarah L.",
      role: "Small Business Owner"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Header Bar */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/images/logo-colored.png" 
                alt="Trademark Liberty" 
                className="h-12 w-auto"
              />
            </div>

            {/* Phone & Chat */}
            <div className="flex items-center space-x-4">
              <a 
                href="tel:+16466872501" 
                className="hidden md:flex items-center space-x-2 text-[#0A1F44] hover:text-[#D4AF37] transition"
              >
                <Phone className="w-5 h-5" />
                <span className="font-semibold">+1 (646) 687-2501</span>
              </a>
              <button
                onClick={openChat}
                className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-semibold px-4 py-2 rounded-lg transition"
              >
                <Mail className="w-5 h-5" />
                <span>Chat Now</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A1F44] via-[#153356] to-[#0A1F44] overflow-hidden pt-20">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left space-y-6">
              {/* Main Headline */}
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-6">
                  <span className="flex items-baseline flex-wrap sm:flex-nowrap">
                    <span className="whitespace-nowrap">Protect Your&nbsp;</span>
                    <span className="text-[#D4AF37] inline-block min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                      {typedText}
                      <span className="animate-pulse">|</span>
                    </span>
                  </span>
                  <span className="block text-2xl sm:text-3xl md:text-4xl mt-1">
                    with a <span className="text-[#D4AF37]">Registered Trademark</span>
                  </span>
                </h1>

                {/* Subheadline */}
                <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
                  Trademark Liberty helps you secure your brand name and logo with full legal protection under U.S. Trademark Law.
                </p>

                {/* Trust Badges - Moved below paragraph */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center space-x-2 text-[#D4AF37]">
                    <Shield className="w-5 h-5" />
                    <span className="text-sm font-medium text-white">USPTO Licensed Attorneys</span>
                  </div>
                  <div className="flex items-center space-x-2 text-[#D4AF37]">
                    <Clock className="w-5 h-5" />
                    <span className="text-sm font-medium text-white">Fast & Hassle-Free Filing</span>
                  </div>
                  <div className="flex items-center space-x-2 text-[#D4AF37]">
                    <Award className="w-5 h-5" />
                    <span className="text-sm font-medium text-white">Flat-Fee Pricing — No Hidden Costs</span>
                  </div>
                </div>
              </div>

              {/* Trust Line */}
              <p className="text-gray-400 text-sm">
                ⭐⭐⭐⭐⭐ Trusted by 5,000+ entrepreneurs and businesses nationwide
              </p>
            </div>

            {/* Right Form - Reduced Width */}
            <div className="flex justify-center lg:justify-end">
              <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md">
                <div className="text-center mb-6">
                  <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-bold px-3 py-1 rounded-full mb-3">
                    ⚡ LIMITED TIME OFFER
                  </div>
                  <h2 className="text-3xl font-bold text-[#0A1F44] mb-2">
                    Get Started for Just $45
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Free consultation • No obligation • Same-day response
                  </p>
                </div>

                {submitSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1F44] mb-3">
                      Thank You!
                    </h3>
                    <p className="text-gray-600">
                      Redirecting you to the application...
                    </p>
                  </div>
                ) : (
                  <form className="space-y-4" onSubmit={handleFormSubmit}>
                    {/* Show detected country */}
                    {detectedCountry && (
                      <div className="text-center text-sm text-gray-600 mb-2">
                        📍 Detected location: <strong>{detectedCountry}</strong>
                      </div>
                    )}

                    {/* Error Message */}
                    {errorMessage && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {errorMessage}
                      </div>
                    )}

                    <div>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition"
                        placeholder="Full Name *"
                      />
                    </div>

                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition"
                        placeholder="Email Address *"
                      />
                    </div>

                    {/* Phone Number with Country Selector */}
                    <div className="relative">
                      <div className="intel-phone-input">
                        <div className="phone-input-container">
                          <button
                            ref={countryButtonRef}
                            type="button"
                            className={`country-selector-btn ${isDetectingCountry ? 'loading' : ''}`}
                            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                            disabled={isDetectingCountry}
                          >
                            {isDetectingCountry ? (
                              <span className="detecting-text">...</span>
                            ) : (
                              <>
                                <span className="selected-flag">{selectedCountry?.flag}</span>
                                <span className="selected-code">{selectedCountry?.phone_code}</span>
                                <ChevronDown className={`dropdown-arrow ${showCountryDropdown ? 'open' : ''}`} />
                              </>
                            )}
                          </button>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Enter phone number *"
                            required
                            disabled={isDetectingCountry}
                          />
                        </div>

                        {showCountryDropdown && (
                          <div ref={dropdownRef} className="country-dropdown">
                            <div className="country-search-container">
                              <Search className="search-icon" />
                              <input
                                type="text"
                                className="country-search"
                                placeholder="Search countries..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                              />
                            </div>
                            <div className="country-list">
                              {popularCountries.length > 0 && (
                                <div className="country-group">
                                  <div className="group-title">Popular Countries</div>
                                  {popularCountries.map((country) => (
                                    <button
                                      key={country.code}
                                      type="button"
                                      className={`country-item ${selectedCountry?.code === country.code ? 'selected' : ''}`}
                                      onClick={() => handleCountrySelect(country)}
                                    >
                                      <span className="country-flag">{country.flag}</span>
                                      <span className="country-name">{country.name}</span>
                                      <span className="country-code">{country.phone_code}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                              
                              {allCountries.length > 0 && (
                                <div className="country-group">
                                  <div className="group-title">All Countries (A-Z)</div>
                                  {allCountries.map((country) => (
                                    <button
                                      key={country.code}
                                      type="button"
                                      className={`country-item ${selectedCountry?.code === country.code ? 'selected' : ''}`}
                                      onClick={() => handleCountrySelect(country)}
                                    >
                                      <span className="country-flag">{country.flag}</span>
                                      <span className="country-name">{country.name}</span>
                                      <span className="country-code">{country.phone_code}</span>
                                    </button>
                                  ))}
                                </div>
                              )}

                              {filteredCountries.length === 0 && (
                                <div className="no-results">
                                  No countries found matching "{searchQuery}"
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {phoneError && (
                        <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                      )}
                    </div>

                    <div>
                      <textarea
                        name="moreInfo"
                        value={formData.moreInfo}
                        onChange={handleInputChange}
                        placeholder="What are you looking to protect? *"
                        rows="3"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting || isDetectingCountry}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold py-4 px-6 rounded-lg text-lg shadow-lg transform transition hover:scale-105 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Submitting...' : '🔒 Claim Your Free Consultation'}
                    </button>

                    <p className="text-center text-xs text-gray-500 mt-3">
                      🔐 Your information is secure and confidential
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Register a Trademark */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6">
              Why Your Business Needs a Registered Trademark
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Federal trademark registration provides powerful legal protection for your brand
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition">
              <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#0A1F44]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Legal Ownership</h3>
              <p className="text-gray-600">
                Prevent others from using your brand name or logo. Establish exclusive rights nationwide.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition">
              <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-[#0A1F44]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Brand Credibility</h3>
              <p className="text-gray-600">
                Build legal ownership and brand credibility that customers and investors trust.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition">
              <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                <FileCheck className="w-7 h-7 text-[#0A1F44]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Amazon & eCommerce Protection</h3>
              <p className="text-gray-600">
                Protect your brand on Amazon, eCommerce platforms, and enroll in Amazon Brand Registry.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition">
              <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                <Scale className="w-7 h-7 text-[#0A1F44]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Federal Law Protection</h3>
              <p className="text-gray-600">
                Gain exclusive nationwide rights under federal law with USPTO registration.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition">
              <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-[#0A1F44]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Business Valuation</h3>
              <p className="text-gray-600">
                Increase your business's valuation and investor trust with registered IP assets.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition">
              <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#0A1F44]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Legal Enforcement</h3>
              <p className="text-gray-600">
                Sue infringers and seek damages in federal court with registered trademark rights.
              </p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={openForm}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold px-8 py-4 rounded-lg text-lg shadow-lg"
            >
              Check If Your Brand Name Is Available
            </button>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6">
              Simple, Fast & Reliable Trademark Registration
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined 4-step process makes trademark registration easy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition h-full">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#0A1F44] font-bold text-xl">
                  1
                </div>
                <div className="w-14 h-14 bg-[#0A1F44] rounded-xl flex items-center justify-center mb-4 mt-4">
                  <FileCheck className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Search & Review</h3>
                <p className="text-gray-600">
                  We conduct a comprehensive trademark search and review to identify potential conflicts and ensure your brand is protected.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition h-full">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#0A1F44] font-bold text-xl">
                  2
                </div>
                <div className="w-14 h-14 bg-[#0A1F44] rounded-xl flex items-center justify-center mb-4 mt-4">
                  <Scale className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Prepare & File</h3>
                <p className="text-gray-600">
                  Our licensed attorneys prepare and file your complete USPTO trademark application with precision.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition h-full">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#0A1F44] font-bold text-xl">
                  3
                </div>
                <div className="w-14 h-14 bg-[#0A1F44] rounded-xl flex items-center justify-center mb-4 mt-4">
                  <Award className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Approval & Protection</h3>
                <p className="text-gray-600">
                  Track your application progress and receive your official trademark registration certificate from the USPTO.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={openForm}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold px-8 py-4 rounded-lg text-lg shadow-lg"
            >
              Start Trademark Registration
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6">
              Why Thousands of Businesses Trust Trademark Liberty
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Scale className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Experienced IP Attorneys</h3>
              <p className="text-gray-600">
                Decades of combined expertise in U.S. trademark law and intellectual property.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Fast Filing Process</h3>
              <p className="text-gray-600">
                Quick turnaround, status tracking, and 24-hour expedited options available.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Transparent Pricing</h3>
              <p className="text-gray-600">
                No hidden costs — clear and upfront fees with flat-rate pricing packages.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-3">Full Legal Support</h3>
              <p className="text-gray-600">
                From search to registration to enforcement — complete trademark protection.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-3">
              <Award className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#0A1F44] font-semibold">Rated ★★★★★ by 500+ entrepreneurs and brand owners nationwide</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-[#D4AF37] fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <div className="font-bold text-[#0A1F44]">{testimonial.author}</div>
                  <div className="text-gray-600 text-sm">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6">
              Affordable, Flat-Fee Trademark Registration Packages
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose a plan that suits your business — all handled by licensed trademark attorneys
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {pricingPackages.map((pkg, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-6 transition-all duration-300 flex flex-col ${
                  pkg.popular
                    ? 'border-4 border-[#D4AF37] shadow-2xl transform lg:-translate-y-4'
                    : 'border-2 border-gray-200 shadow-lg hover:shadow-2xl hover:-translate-y-2'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#D4AF37] text-[#0A1F44] px-4 py-1 rounded-full font-bold text-sm whitespace-nowrap">
                      MOST POPULAR
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#0A1F44] mb-2">{pkg.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-[#0A1F44]">{pkg.price}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{pkg.priceNote}</p>
                  <p className="text-gray-600 text-sm">{pkg.description}</p>
                </div>

                <ul className="space-y-3 mb-6 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={openForm}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold py-3 px-6 rounded-lg transition mt-auto"
                >
                  Start Registration
                </button>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-600 text-sm">
              <strong>Note:</strong> USPTO government filing fee ($250–$350 per class) not included in package prices.
            </p>
          </div>
        </div>
      </section>

      {/* Lead Capture Form */}
      <section id="lead-form" className="py-20 bg-gradient-to-br from-[#0A1F44] to-[#153356]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Start Your Trademark Registration Now
            </h2>
            <p className="text-xl text-gray-300">
              Complete the form below and our trademark attorney will contact you within 24 hours.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
            {submitSuccess ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-3xl font-bold text-[#0A1F44] mb-4">
                  Thank You!
                </h3>
                <p className="text-lg text-gray-600 mb-4">
                  Your request has been received. A Trademark Liberty representative will contact you within 24 hours to begin your registration process.
                </p>
                <p className="text-sm text-gray-500">
                  Check your email for confirmation details.
                </p>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleFormSubmit}>
                {/* Error Message */}
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {errorMessage}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName2" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName2"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email2" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email2"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone2" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="intel-phone-input">
                    <div className="phone-input-container">
                      <button
                        ref={countryButtonRef}
                        type="button"
                        className={`country-selector-btn ${isDetectingCountry ? 'loading' : ''}`}
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        disabled={isDetectingCountry}
                      >
                        {isDetectingCountry ? (
                          <span className="detecting-text">...</span>
                        ) : (
                          <>
                            <span className="selected-flag">{selectedCountry?.flag}</span>
                            <span className="selected-code">{selectedCountry?.phone_code}</span>
                            <ChevronDown className={`dropdown-arrow ${showCountryDropdown ? 'open' : ''}`} />
                          </>
                        )}
                      </button>
                      <input
                        type="tel"
                        id="phone2"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                        required
                        disabled={isDetectingCountry}
                      />
                    </div>

                    {showCountryDropdown && (
                      <div ref={dropdownRef} className="country-dropdown">
                        <div className="country-search-container">
                          <Search className="search-icon" />
                          <input
                            type="text"
                            className="country-search"
                            placeholder="Search countries..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>
                        <div className="country-list">
                          {popularCountries.length > 0 && (
                            <div className="country-group">
                              <div className="group-title">Popular Countries</div>
                              {popularCountries.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  className={`country-item ${selectedCountry?.code === country.code ? 'selected' : ''}`}
                                  onClick={() => handleCountrySelect(country)}
                                >
                                  <span className="country-flag">{country.flag}</span>
                                  <span className="country-name">{country.name}</span>
                                  <span className="country-code">{country.phone_code}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {allCountries.length > 0 && (
                            <div className="country-group">
                              <div className="group-title">All Countries (A-Z)</div>
                              {allCountries.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  className={`country-item ${selectedCountry?.code === country.code ? 'selected' : ''}`}
                                  onClick={() => handleCountrySelect(country)}
                                >
                                  <span className="country-flag">{country.flag}</span>
                                  <span className="country-name">{country.name}</span>
                                  <span className="country-code">{country.phone_code}</span>
                                </button>
                              ))}
                            </div>
                          )}

                          {filteredCountries.length === 0 && (
                            <div className="no-results">
                              No countries found matching "{searchQuery}"
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="moreInfo2" className="block text-sm font-semibold text-gray-700 mb-2">
                    What are you looking to protect? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="moreInfo2"
                    name="moreInfo"
                    value={formData.moreInfo}
                    onChange={handleInputChange}
                    placeholder="Tell us about your trademark, brand name, logo, or other intellectual property needs..."
                    rows="4"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition resize-none"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting || isDetectingCountry}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold py-4 px-8 rounded-lg text-lg shadow-lg transform transition hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Secure My Trademark'}
                  </button>
                </div>

                <p className="text-center text-sm text-gray-600">
                  By submitting, you agree to receive communications from Trademark Liberty regarding your trademark registration.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Minimalist Footer */}
      <footer className="bg-[#0A1F44] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img 
                src="/images/logo-white.png" 
                alt="Trademark Liberty" 
                className="h-14 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Safeguarding Innovation. Protecting Identity. Your trusted partner in intellectual property law.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-sm text-gray-400">Call Us</div>
                    <a href="tel:+16466872501" className="text-white hover:text-[#D4AF37] transition">
                      +1 (646) 687-2501
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-sm text-gray-400">Email</div>
                    <a href="mailto:info@trademarkliberty.com" className="text-white hover:text-[#D4AF37] transition">
                      info@trademarkliberty.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-sm text-gray-400">Address</div>
                    <div className="text-white text-sm">
                      123 Liberty Street, Suite 500<br />
                      New York, NY 10006
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Secured Payment Methods</h4>
              <div className="flex items-center gap-6 flex-wrap">
                {/* Visa */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_9829285a-4e8d-43c1-8aa4-dfba5e9e8459/artifacts/lioz0blv_visa_inc_logo.svg.png" 
                  alt="Visa" 
                  className="h-10 w-auto object-contain"
                />
                {/* Mastercard */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_9829285a-4e8d-43c1-8aa4-dfba5e9e8459/artifacts/nxa3u9j1_Mastercard.png" 
                  alt="Mastercard" 
                  className="h-10 w-auto object-contain"
                />
                {/* American Express */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_9829285a-4e8d-43c1-8aa4-dfba5e9e8459/artifacts/arzc9wj7_American%20Express.png" 
                  alt="American Express" 
                  className="h-10 w-auto object-contain"
                />
                {/* Discover */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_9829285a-4e8d-43c1-8aa4-dfba5e9e8459/artifacts/i61v5gii_Discover.png" 
                  alt="Discover" 
                  className="h-10 w-auto object-contain"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-6">
            <p className="text-gray-400 text-sm text-center">
              © {new Date().getFullYear()} Trademark Liberty. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Consultation Form Modal */}
      <ConsultationForm 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        formType="registration"
      />
    </div>
  );
};
